import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";

import { create } from "@/lib/store";
import { ChartPeriod } from "@/shared/types";
import { hashStorage } from "@/Utils/hashStorage";
import { GeoViewToggle } from "@/Utils/sharedTypes";

import { ChartToggle, ROIWindow } from "../types/common";

const STORE_NAME = "Flighting";

interface FlightingState {
  filters: {
    targetNode: string | null;
    channels: string[];
    funnelStage: string[];
    geographies: string[];
  };
  chart: {
    chartPeriod: ChartPeriod;
    ROIWindow: ROIWindow;
    toggles: ChartToggle[];
  };
  grid: {
    geoView: GeoViewToggle;
  };
}

interface FlightingActions {
  setTargetNode: (node: string | null) => void;
  setFunnelStage: (funnelStage: string[]) => void;
  setChannels: (channels: string[]) => void;
  setGeographies: (geographies: string[]) => void;
  setChartPeriod: (chartPeriod: ChartPeriod) => void;
  setROIWindow: (ROIWindow: ROIWindow) => void;
  setGeoView: (geoView: GeoViewToggle) => void;
  setToggles: (toggles: ChartToggle[]) => void;
}

type FlightingStore = FlightingState & FlightingActions;

const defaultState: FlightingState = {
  filters: {
    targetNode: null,
    funnelStage: [],
    channels: [],
    geographies: [],
  },
  chart: {
    chartPeriod: ChartPeriod.WEEKLY,
    ROIWindow: 12,
    toggles: [ChartToggle.SPEND, ChartToggle.ROI],
  },
  grid: {
    geoView: GeoViewToggle.AGGREGATE,
  },
};

export const useFlightingStore = create<FlightingStore>()(
  devtools(
    persist(
      immer((set) => ({
        ...defaultState,
        setTargetNode: (node) =>
          set((state) => {
            state.filters.targetNode = node;
          }),
        setFunnelStage: (funnelStage) =>
          set((state) => {
            state.filters.funnelStage = funnelStage;
          }),
        setChannels: (channels) =>
          set((state) => {
            state.filters.channels = channels;
          }),
        setGeographies: (geographies) =>
          set((state) => {
            state.filters.geographies = geographies;
          }),
        setChartPeriod: (chartPeriod) =>
          set((state) => {
            state.chart.chartPeriod = chartPeriod;
          }),
        setROIWindow: (ROIWindow) =>
          set((state) => {
            state.chart.ROIWindow = ROIWindow;
          }),
        setGeoView: (geoView) =>
          set((state) => {
            state.grid.geoView = geoView;
          }),
        setToggles: (chartToggles) =>
          set((state) => {
            state.chart.toggles = chartToggles;
          }),
      })),
      {
        name: STORE_NAME,
        storage: createJSONStorage(() => hashStorage),
      }
    ),
    { name: STORE_NAME }
  )
);
